import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, ComposedChart, Line, Cell,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtINR = (v) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v || 0);
const fmtCompact = (v) => {
  if (!v) return "₹0";
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(2)}L`;
  if (abs >= 1e3) return `${sign}₹${(abs / 1e3).toFixed(1)}K`;
  return `${sign}₹${Math.round(abs)}`;
};
const fmtPct = (v) => v == null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`;

const SectionCard = ({ title, children, testid, className = "" }) => (
  <div className={`bg-[#1A1A1A] border border-[#2A2A2A] rounded p-5 ${className}`} data-testid={testid}>
    {title && <h3 className="text-sm font-semibold text-white mb-4 tracking-wide uppercase">{title}</h3>}
    {children}
  </div>
);

const KpiCard = ({ label, value, sub, accent = "white", testid }) => (
  <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1">{label}</div>
    <div className={`text-2xl font-semibold tabular-nums`} style={{ color: accent }}>{value}</div>
    {sub && <div className="text-[11px] text-[#71717A] mt-1">{sub}</div>}
  </div>
);

const ChartTooltip = ({ active, payload, label, view }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0F0F0F] border border-[#2A2A2A] rounded p-3 text-xs space-y-1">
      <div className="text-white font-semibold mb-1">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-sm" style={{ background: p.color || p.fill }} />
          <span className="text-[#A1A1AA]">{p.name}:</span>
          <span className="text-white tabular-nums">{fmtINR(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const Insights = ({ insights }) => {
  if (!insights) return null;
  const blocks = [
    { key: "top5_performance_contributors", title: "Top 5 — Performance Fee", color: "#00E676", testid: "insights-top5-perf" },
    { key: "top5_fixed_contributors", title: "Top 5 — Fixed Fee", color: "#FFEB00", testid: "insights-top5-fixed" },
    { key: "declining_clients", title: "Declining 3+ Months", color: "#FF6D00", testid: "insights-declining" },
    { key: "beating_expected", title: "Beating Expected (>5%)", color: "#00E676", testid: "insights-beating" },
    { key: "below_expected", title: "Below Expected (<-5%)", color: "#FF4444", testid: "insights-below" },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {blocks.map(b => {
        const items = insights[b.key] || [];
        return (
          <SectionCard key={b.key} title={b.title} testid={b.testid} className="">
            {items.length === 0 ? (
              <p className="text-xs text-[#71717A]">No clients in this category.</p>
            ) : (
              <ul className="space-y-2" data-testid={`${b.testid}-list`}>
                {items.map((c, i) => (
                  <li key={c.client_id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="text-[#71717A] w-4">{i + 1}.</span>
                      <span className="text-white truncate" title={c.client_name}>{c.client_name}</span>
                    </span>
                    <span className="text-right shrink-0">
                      {"share_pct" in c && (
                        <div className="text-white tabular-nums">{fmtINR(c.amount)}</div>
                      )}
                      {"share_pct" in c && (
                        <div className="text-[10px] text-[#71717A]">{c.share_pct}% of total</div>
                      )}
                      {"diff_pct" in c && (
                        <>
                          <div className="text-white tabular-nums">{fmtINR(c.actual)}</div>
                          <div className="text-[10px]" style={{ color: c.diff_pct >= 0 ? "#00E676" : "#FF4444" }}>
                            {fmtPct(c.diff_pct)} vs expected
                          </div>
                        </>
                      )}
                      {"decline_streak" in c && (
                        <>
                          <div className="text-white tabular-nums">{fmtINR(c.latest)}</div>
                          <div className="text-[10px] text-[#FF6D00]">
                            {c.decline_streak}m streak · {fmtPct(c.drop_pct)}
                          </div>
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        );
      })}
    </div>
  );
};

const DrillDownModal = ({ month, breakdown, onClose }) => {
  if (!month) return null;
  const rows = (breakdown || []).slice().sort((a, b) => b.total - a.total);
  const total = rows.reduce((s, r) => s + r.total, 0);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose} data-testid="drilldown-overlay">
      <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-[#2A2A2A] flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-white">{month.label} — Client Breakdown</h3>
            <p className="text-[11px] text-[#71717A]">Total: <span className="text-white">{fmtINR(total)}</span> · {rows.length} clients</p>
          </div>
          <button onClick={onClose} className="text-[#71717A] hover:text-white text-xl leading-none px-2" data-testid="drilldown-close">×</button>
        </div>
        <div className="overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#0F0F0F] text-[#71717A] uppercase tracking-wider text-[10px] sticky top-0">
              <tr>
                <th className="px-4 py-2 text-left">Client</th>
                <th className="px-4 py-2 text-right">Fixed</th>
                <th className="px-4 py-2 text-right">Performance</th>
                <th className="px-4 py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.client_id} className="border-t border-[#2A2A2A] hover:bg-[#0F0F0F]" data-testid={`drilldown-row-${r.client_id}`}>
                  <td className="px-4 py-2 text-white">{r.client_name}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-[#FFEB00]">{fmtINR(r.fixed)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-[#00E676]">{fmtINR(r.performance)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-white">{fmtINR(r.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const BillingTrends = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("actual"); // actual | expected | compare
  const [drillDown, setDrillDown] = useState(null); // {month, label} | null
  const [error, setError] = useState(null);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(""); // "" = all clients

  // Load clients for the filter dropdown
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/clients`);
        const raw = Array.isArray(r.data) ? r.data : (r.data?.clients || []);
        const list = raw.map(c => ({ id: c.id, name: c.client_name || c.name }))
          .filter(c => c.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        if (!cancelled) setClients(list);
      } catch (e) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ months: "6" });
        if (selectedClient) params.set("client_id", selectedClient);
        const r = await axios.get(`${API}/dashboard/billing-trends?${params.toString()}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "Failed to load billing trends");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedClient]);

  const chartData = useMemo(() => {
    if (!data?.months) return [];
    return data.months.map(m => ({
      month: m.month_label,
      monthKey: m.month,
      fixed: view === "expected" ? m.expected_fixed : m.actual_fixed,
      performance: view === "expected" ? m.expected_performance : m.actual_performance,
      total: view === "expected" ? m.expected_total : m.actual_total,
      actual_total: m.actual_total,
      expected_total: m.expected_total,
      fixed_pct: m.fixed_pct,
      performance_pct: m.performance_pct,
      is_current: m.is_current,
    }));
  }, [data, view]);

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-8 w-64 bg-[#1A1A1A] rounded animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1,2,3,4].map(i => <div key={i} className="h-24 bg-[#1A1A1A] rounded animate-pulse" />)}
        </div>
        <div className="h-80 bg-[#1A1A1A] rounded animate-pulse" />
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-sm text-[#FF6666]" data-testid="billing-trends-error">{error}</div>;
  }

  if (!data) return null;
  const s = data.summary || {};
  const months = data.months || [];

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto" data-testid="billing-trends-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">6-Month Billing Trend</h1>
          <p className="text-[11px] text-[#71717A] mt-1">
            Fixed Fee vs Performance Fee · {months[0]?.month_label} → {months[months.length - 1]?.month_label} · All amounts in ₹
            {selectedClient && (
              <span className="ml-2 text-[#FFEB00]">
                · Scoped to {clients.find(c => c.id === selectedClient)?.name || "selected client"}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase tracking-wider text-[#71717A]">Client</label>
            <select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="bg-[#1A1A1A] border border-[#2A2A2A] text-white text-xs rounded px-3 py-1.5 hover:border-[#FFEB00]/40 focus:border-[#FFEB00] focus:outline-none min-w-[200px]"
              data-testid="client-filter"
            >
              <option value="">All clients</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {selectedClient && (
              <button
                onClick={() => setSelectedClient("")}
                className="text-[11px] text-[#71717A] hover:text-white px-1"
                title="Clear filter"
                data-testid="client-filter-clear"
              >
                ×
              </button>
            )}
          </div>
          <div className="flex items-center gap-1 bg-[#1A1A1A] border border-[#2A2A2A] rounded p-1" data-testid="view-toggle">
            {[
              ["actual", "Actual"],
              ["expected", "Expected"],
              ["compare", "Actual vs Expected"],
            ].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setView(k)}
                className={`px-3 py-1.5 text-xs rounded transition-colors ${
                  view === k ? "bg-[#FFEB00] text-black font-semibold" : "text-[#A1A1AA] hover:text-white"
                }`}
                data-testid={`view-toggle-${k}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        <KpiCard
          label="6-Month Total"
          value={fmtCompact(s.total_billing)}
          sub={fmtINR(s.total_billing)}
          accent="#FFEB00"
          testid="kpi-total"
        />
        <KpiCard
          label="Fixed Fee"
          value={fmtCompact(s.total_fixed)}
          sub={`${s.fixed_mix_pct || 0}% of mix`}
          accent="#FFEB00"
          testid="kpi-fixed"
        />
        <KpiCard
          label="Performance Fee"
          value={fmtCompact(s.total_performance)}
          sub={`${s.performance_dependency_pct || 0}% dependency`}
          accent="#00E676"
          testid="kpi-perf"
        />
        <KpiCard
          label="Avg / Month"
          value={fmtCompact(s.avg_monthly)}
          sub={fmtINR(s.avg_monthly)}
          accent="white"
          testid="kpi-avg"
        />
        <KpiCard
          label="Best Month"
          value={s.best_month?.month_label || "—"}
          sub={s.best_month ? fmtCompact(s.best_month.actual_total) : ""}
          accent="#00E676"
          testid="kpi-best"
        />
        <KpiCard
          label="Lowest Month"
          value={s.lowest_month?.month_label || "—"}
          sub={s.lowest_month ? fmtCompact(s.lowest_month.actual_total) : ""}
          accent="#FF6D00"
          testid="kpi-lowest"
        />
        <KpiCard
          label="Current vs Prev"
          value={fmtPct(s.current_vs_prev_pct)}
          sub={s.current_vs_prev_abs != null ? fmtCompact(s.current_vs_prev_abs) : ""}
          accent={(s.current_vs_prev_pct ?? 0) >= 0 ? "#00E676" : "#FF4444"}
          testid="kpi-vs-prev"
        />
      </div>

      {/* Main chart — Fixed + Performance side by side with Total line */}
      <SectionCard
        title={view === "compare" ? "Actual vs Expected (Total Billing)" : view === "expected" ? "Expected Billing (Fixed vs Performance)" : "Actual Billing (Fixed vs Performance)"}
        testid="billing-trend-chart"
      >
        <ResponsiveContainer width="100%" height={380}>
          {view === "compare" ? (
            <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
              <XAxis dataKey="month" stroke="#A1A1AA" fontSize={11} />
              <YAxis stroke="#A1A1AA" fontSize={11} tickFormatter={fmtCompact} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: "#FFFFFF08" }} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#A1A1AA" }} />
              <Bar
                dataKey="actual_total" name="Actual"
                fill="#FFEB00" radius={[4, 4, 0, 0]} maxBarSize={56}
                onClick={(p) => p && setDrillDown({ month: p.monthKey, label: p.month })}
                cursor="pointer"
                data-testid="bar-actual"
              />
              <Bar
                dataKey="expected_total" name="Expected"
                fill="#3B82F6" radius={[4, 4, 0, 0]} maxBarSize={56}
                cursor="pointer"
                data-testid="bar-expected"
              />
            </ComposedChart>
          ) : (
            <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
              <XAxis dataKey="month" stroke="#A1A1AA" fontSize={11} />
              <YAxis stroke="#A1A1AA" fontSize={11} tickFormatter={fmtCompact} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: "#FFFFFF08" }} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#A1A1AA" }} />
              <Bar
                dataKey="fixed" name="Fixed Fee"
                fill="#FFEB00" radius={[4, 4, 0, 0]} maxBarSize={42}
                onClick={(p) => p && setDrillDown({ month: p.monthKey, label: p.month })}
                cursor="pointer"
                data-testid="bar-fixed"
              />
              <Bar
                dataKey="performance" name="Performance Fee"
                fill="#00E676" radius={[4, 4, 0, 0]} maxBarSize={42}
                onClick={(p) => p && setDrillDown({ month: p.monthKey, label: p.month })}
                cursor="pointer"
                data-testid="bar-performance"
              />
              <Line
                type="monotone" dataKey="total" name="Total"
                stroke="#FF6D00" strokeWidth={2}
                dot={{ fill: "#FF6D00", r: 4 }}
                activeDot={{ r: 6 }}
              />
            </ComposedChart>
          )}
        </ResponsiveContainer>
        <p className="text-[10px] text-[#71717A] mt-2">Click any bar to drill down into per-client billing for that month.</p>
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Billing Mix by Month — stacked percentages */}
        <SectionCard title="Billing Mix by Month" testid="billing-mix-chart">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={chartData.map(m => ({
                month: m.month,
                "Fixed %": m.fixed_pct,
                "Performance %": m.performance_pct,
              }))}
              margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
              stackOffset="expand"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
              <XAxis dataKey="month" stroke="#A1A1AA" fontSize={11} />
              <YAxis stroke="#A1A1AA" fontSize={11} tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} />
              <Tooltip
                formatter={(v) => `${v}%`}
                contentStyle={{ background: "#0F0F0F", border: "1px solid #2A2A2A", borderRadius: 4, fontSize: 11 }}
              />
              <Legend wrapperStyle={{ fontSize: 11, color: "#A1A1AA" }} />
              <Bar dataKey="Fixed %" stackId="mix" fill="#FFEB00" data-testid="mix-bar-fixed" />
              <Bar dataKey="Performance %" stackId="mix" fill="#00E676" data-testid="mix-bar-perf" />
            </BarChart>
          </ResponsiveContainer>
        </SectionCard>

        {/* Expected vs Actual — focused chart */}
        <SectionCard title="Expected vs Actual Billing" testid="expected-vs-actual-chart">
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
              <XAxis dataKey="month" stroke="#A1A1AA" fontSize={11} />
              <YAxis stroke="#A1A1AA" fontSize={11} tickFormatter={fmtCompact} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: "#FFFFFF08" }} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#A1A1AA" }} />
              <Bar dataKey="expected_total" name="Expected" fill="#3B82F6" radius={[4, 4, 0, 0]} maxBarSize={42} />
              <Line type="monotone" dataKey="actual_total" name="Actual" stroke="#00E676" strokeWidth={2} dot={{ fill: "#00E676", r: 4 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </SectionCard>
      </div>

      {/* Month-on-Month growth strip */}
      <SectionCard title="Month-on-Month Growth" testid="mom-growth-chart">
        <ResponsiveContainer width="100%" height={180}>
          <BarChart
            data={(s.mom_growth || []).map((g, i) => ({
              month: months[i]?.month_label || g.month,
              growth: g.growth_pct,
            }))}
            margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
            <XAxis dataKey="month" stroke="#A1A1AA" fontSize={11} />
            <YAxis stroke="#A1A1AA" fontSize={11} tickFormatter={(v) => v == null ? "" : `${v}%`} />
            <Tooltip
              formatter={(v) => v == null ? "—" : `${v}%`}
              contentStyle={{ background: "#0F0F0F", border: "1px solid #2A2A2A", borderRadius: 4, fontSize: 11 }}
            />
            <Bar dataKey="growth" radius={[4, 4, 0, 0]} maxBarSize={64}>
              {(s.mom_growth || []).map((g, i) => (
                <Cell key={i} fill={(g.growth_pct ?? 0) >= 0 ? "#00E676" : "#FF4444"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </SectionCard>

      {/* Insights */}
      <Insights insights={data.insights} />

      {/* Drill-down modal */}
      {drillDown && (
        <DrillDownModal
          month={drillDown}
          breakdown={data.client_breakdowns?.[drillDown.month] || []}
          onClose={() => setDrillDown(null)}
        />
      )}
    </div>
  );
};

export default BillingTrends;
